import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { take, filter } from 'rxjs/operators';

// services
import { EventsService } from '../../core/services/events-service/events.service';
import { ProfileService } from '../../core/services/profile-service/profile.service';

// components
import { AlertComponent } from '../../shared';

// constants
import {
	tshirtSizes,
	CalculateAge,
	ScrollIt,
	scrollItType,
	getPhoneMask,
} from '../../shared';

const mockData = {
	email: 'luis@ez-events.com',
	name_first: 'Luis',
	name_middle: 'Alfredo',
	name_last: 'Pena',
	address: '123 Main St',
	address_2: 'Apt 101',
	city: 'Los Angeles',
	state: 'CA',
	zip: '90017',
	phone: '323-863-3359',
	birth_date: '1980-09-19',
	subscription: false,
	shirt_size: 'Adult M',
	parent_name_first: 'luis',
	parent_name_middle: 'M',
	parent_name_last: 'Pena',
	parent_email: 'otilia@luispena.com',
	parent_phone: '323-863-3559',
	school: {
		id: 14458,
	},
	legal_age: true,
	no_crime: true,
	waiver: true,
	terms_and_conditions: true,
	catchall: true,
};

interface School {
	id: string;
	name: string;
	city: string;
}

@Component({
	selector: 'eze-master-registration',
	templateUrl: './master-registration.component.html',
	styleUrls: ['./master-registration.component.css'],
})
export class MasterRegistrationComponent implements OnInit {
	form: FormGroup;
	showValidation = false;
	eventId = '';
	successHtml = '';

	@ViewChild(AlertComponent, { static: false })
	alert: AlertComponent;
	@ViewChild('waiverModal', { static: false })
	waiverModal;
	@ViewChild('termsModal', { static: false })
	termsModal;

	get shirtSizes(): string[] {
		return tshirtSizes;
	}

	get waiverText(): string {
		return this.eventService.waiver.text;
	}

	get waiverHeader(): string {
		return this.eventService.waiver.header;
	}

	get ranks(): { value: number; desc: string }[] {
		return this.eventService.eventData.masters.ranks;
	}

	get isClosed(): boolean {
		return this.eventService.isRegistrationClosed('master');
	}

	get eventUrl(): string {
		return this.eventService.eventData.url;
	}

	get profile() {
		return this.profileService.profile;
	}

	get isGuest() {
		return this.profileService.isGuest;
	}

	get schools() {
		return this.eventService.schools;
	}

	get message(): string | undefined {
		return (
			this.eventService.eventData.registrations.masters &&
			this.eventService.eventData.registrations.masters.message
		);
	}

	constructor(
		private fb: FormBuilder,
		@Inject(CalculateAge) private calculateAge,
		@Inject(ScrollIt) private scrollIt: scrollItType,
		private eventService: EventsService,
		private route: ActivatedRoute,
		private profileService: ProfileService
	) {}

	ngOnInit() {
		this.getEventId();
		this.initializeForm();
		this.patchFormWithProfile();
		this.profileService.patchFormWithProfile(this.form);
	}

	patchFormWithProfile() {
		this.profileService.profile
			.pipe(
				take(1),
				filter((data) => !!data)
			)
			.subscribe((data) => this.form.patchValue(data));
	}

	getEventId() {
		this.eventId = this.route.snapshot.parent.params.id;
	}

	documentIdRequired() {
		return {};
		// return { document_id: ['', Validators.required] };
	}

	initializeForm() {
		this.form = this.fb.group(
			{
				// personal information
				name_first: ['', Validators.required],
				name_middle: [''],
				name_last: ['', Validators.required],

				birth_date: ['', Validators.required],

				gender: ['', Validators.required],
				rank: [''],
				cert: ['', Validators.required],

				// contact information
				email: ['', [Validators.required, Validators.email]],
				phone: ['', Validators.required],
				address: ['', Validators.required],
				address_2: [''],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required],

				...this.documentIdRequired(),

				// misc information
				// 'shirt_size': 			['', Validators.required],
				school: this.fb.group({
					id: ['', Validators.required],
				}),

				// parent information
				parent_name_first: [''],
				parent_name_middle: [''],
				parent_name_last: [''],
				parent_email: [''],
				parent_phone: [''],

				// legal stuff and subsrcriptions
				subscription: [false],
				legal_age: [false, Validators.requiredTrue],
				no_crime: [false, Validators.requiredTrue],
				waiver: [false, Validators.requiredTrue],
				terms_and_conditions: [false, Validators.requiredTrue],
				catchall: [false, Validators.requiredTrue],
			},
			{
				validator: (control) => this.parentRequired(control),
			}
		);

		// console.log('adding mock data');
		// this.form.patchValue(mockData);
	}

	parentRequired(control: FormGroup) {
		const value = control.value;
		if (!this.form || !this.isMinor(this.form)) {
			return null;
		}

		const parentFields = [
			'parent_name_first',
			'parent_name_last',
			'parent_email',
			'parent_phone',
		];

		const output = {};

		parentFields.forEach((field) => {
			if (value[field] === '') {
				output[field] = { required: true };
			}
		});

		if (Object.keys(output).length > 0) {
			return output;
		} else {
			return null;
		}
	}

	onSubmit() {
		this.scrollIt(0);
		if (this.form.invalid) {
			this.alert.newAlert('Please fill out required fields', 'danger');
			this.showValidation = true;
			return;
		}

		this.alert.newAlert('Submitting...', 'info');
		this.eventService
			.postEntity(this.eventId, 'masters', this.form.value)
			.subscribe(
				(res: any) => {
					if (res && res._html_message) {
						this.successHtml = res._html_message;
					} else {
						this.alert.newAlert('There was an error', 'danger');
					}
					// TODO - add type to res
				},
				(err) => {
					this.alert.newAlert('There was an error', 'danger');
				}
			);
	}

	resetForm() {
		this.form.reset();
		this.successHtml = '';
		this.showValidation = false;
	}

	isMinor(formGroup: FormGroup): boolean {
		const birth_date = formGroup.get('birth_date').value;
		return this.calculateAge(new Date(birth_date)) < 18;
	}

	openModal(event: Event, modalName: string) {
		event.preventDefault();
		switch (modalName) {
			case 'waiver':
				this.waiverModal.open();
				break;
			case 'terms':
				this.termsModal.open();
				break;
			default:
				break;
		}
	}

	getPhoneMask(formControl) {
		return getPhoneMask(this.form.get(formControl).value);
	}
}
